/**
 * DriverDashboard - KPI cards + next mission preview for driver home tab
 */
import React, { useState, useEffect } from 'react';
import { Map, TrendingUp, Calendar, ArrowRight, AlertCircle } from 'lucide-react';
import { driverMonoAPI } from '../../../api/missions.js';
import { useFormat } from '../../../i18n/format.js';

export default function DriverDashboard({ onNavigateToMission }) {
  const { fmt } = useFormat();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const res = await driverMonoAPI.getStats();
      setStats(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
        <p className="text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  const kpis = [
    {
      label: 'Missions ce mois',
      value: stats?.missions_this_month || 0,
      icon: Calendar,
      color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    },
    {
      label: 'Missions actives',
      value: stats?.missions_active || 0,
      icon: Map,
      color: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    },
    {
      label: 'Total missions',
      value: stats?.missions_completed || 0,
      icon: TrendingUp,
      color: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
    },
    {
      label: 'Km parcourus',
      value: `${Math.round(stats?.total_km_driven || 0)} km`,
      icon: TrendingUp,
      color: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    }
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <div key={idx} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4">
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${kpi.color} mb-3`}>
                <Icon className="h-5 w-5" />
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{kpi.value}</p>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">{kpi.label}</p>
            </div>
          );
        })}
      </div>

      {/* Next Mission Card */}
      {stats?.next_mission && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
          <h3 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-3">Prochaine mission</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {stats.next_mission.title}
              </p>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                {stats.next_mission.code} - {stats.next_mission.planned_start
                  ? fmt.date(stats.next_mission.planned_start, {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })
                  : 'Date non planifiée'}
              </p>
              <span className={`inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                stats.next_mission.status === 'assigned' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                stats.next_mission.status === 'accepted' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300'
              }`}>
                {stats.next_mission.status === 'assigned' ? 'En attente' :
                 stats.next_mission.status === 'accepted' ? 'Acceptée' :
                 stats.next_mission.status}
              </span>
            </div>
            <button
              onClick={() => onNavigateToMission(stats.next_mission.id)}
              className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Voir <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!stats?.next_mission && stats?.missions_active === 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-8 text-center">
          <Map className="h-12 w-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-slate-400">Aucune mission en cours ou planifiée</p>
        </div>
      )}
    </div>
  );
}
